import { useEffect, useState } from "react";
import {
  Address,
  beginCell,
  Cell,
  fromNano,
  toNano,
  OpenedContract,
} from "@ton/core";
import { useAsyncInitialize } from "./useAsyncInitialize";
import { useTonClient } from "./useTonCLient";
import { useTonConnect } from "./useTonConnect";
import { addNft, makeSnakeAddressCellPerOne } from "../utils/storage";
import { sleep } from "../utils/common";
import { SimpleNftCollectionV2 } from "../contracts/SimpleNftMasterV2/tact_SimpleNftCollectionV2";
import {
  getCollectionDataFromBackend,
  getCollectionMasterData,
} from "../functions/mintCollection";
import { backend } from "../services/backend";
import { BuyerProfile } from "@/contracts/SimpleNftMasterV2/tact_BuyerProfile";
import { sendGtag } from "@/utils/stats";
import { useOwnerWhitelist } from "./useOwnerWhitelist";

export const requestProfilePrice = "0.03"; // 0.025
export const minCollectionItemPrice = "0.05";
export const minCollectionItemPriceWhitelist = "0.1";
export const whitelistActionPriceAdd = "0.004";
export const whitelistActionPriceRemove = "0.005";
export const whitelistWalletTransactionReserve = "0.005";
export const whitelistWalletTransactionReserveSingle = "0.02";

export function useCollectionContract() {
  const { client } = useTonClient();
  const { wallet, sender } = useTonConnect();
  const [balance, setBalance] = useState<string | null>(null);

  // Collection hook
  const customCollection = (address: string) => {
    return useAsyncInitialize(async () => {
      if (!client || !wallet) return;

      const contract: any = SimpleNftCollectionV2.fromAddress(
        Address.parse(address)
      );
      return client.open(contract) as OpenedContract<SimpleNftCollectionV2>;
    }, [client, wallet]);
  };

  const getAddressBalance = async (walletAddress: string) => {
    console.log("Client:", client);
    const balance = await client?.getBalance(Address.parse(walletAddress));
    console.log("Taken balance:", balance);
    return balance ? Number(balance) : undefined; // bigint в нанотонах
  };

  const getMasterData = async (address: string) => {
    try {
      const data = await customCollection(address)?.getGetMasterData();
      return data;
    } catch (e: any) {
      return null;
    }
  };

  const getNftAddressByIndex = (address: string, index: number) => {
    return customCollection(address)?.getGetNftAddressByIndex(BigInt(index));
  };

  const withdrawEstimate = async (collection: string, addressTo: string) => {
    const contract = SimpleNftCollectionV2.fromAddress(
      Address.parse(collection)
    );
    sendGtag("request_withdraw", {
      collection,
    });
    const opened = client?.open(
      contract
    ) as OpenedContract<SimpleNftCollectionV2>;
    if (!opened) {
      return false;
    }
    try {
      await opened?.send(
        sender,
        {
          value: toNano(requestProfilePrice),
        },
        {
          $$type: "Withdraw",
          to: Address.parse(addressTo),
        }
      );
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const getCollectionData = (address: string) => {
    const cleanAddress = address.split('?')[0]
    return customCollection(cleanAddress)?.getGetCollectionData();
  };

  const getNftContent = (address: string, index: number) => {
    return customCollection(address)?.getGetNftContent(
      BigInt(index),
      new Cell({})
    );
  };

  const requestProfile = async (address: string, callback?: () => void) => {
    const cleanAddress = address.split('?')[0]
    const contract = SimpleNftCollectionV2.fromAddress(Address.parse(cleanAddress));
    const opened = client?.open(
      contract
    ) as OpenedContract<SimpleNftCollectionV2>;
    if (!opened) {
      return false;
    }
    sendGtag("request_whitelist", {
      collection: cleanAddress,
      wallet: sender.address,
    });
    try {
      await opened?.send(
        sender,
        {
          value: toNano(requestProfilePrice),
        },
        "RequestWhitelist"
      );
      setTimeout(() => {
        if (callback) callback();
      }, 20000);
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const updateWhitelist = async (
    wallet: string,
    collection: string,
    whitelist = true
  ) => {
    const contract = SimpleNftCollectionV2.fromAddress(
      Address.parse(collection)
    );
    const opened = client?.open(
      contract
    ) as OpenedContract<SimpleNftCollectionV2>;
    const actionValue = whitelist
      ? whitelistActionPriceAdd
      : whitelistActionPriceRemove;
    const sendingvalue =
      Number(actionValue) + Number(whitelistWalletTransactionReserveSingle);
    if (!opened) {
      return false;
    }
    try {
      await opened?.send(
        sender,
        {
          value: toNano(sendingvalue),
        },
        {
          $$type: "UpdateWhiteList",
          user: Address.parse(wallet),
          whitelist,
        }
      );
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const calcMassUpdateValue = (count: number, isAdd: boolean) => {
    const actionValue = isAdd
      ? whitelistActionPriceAdd
      : whitelistActionPriceRemove;
    const total =
      (Number(actionValue) + Number(whitelistWalletTransactionReserve)) *
        count +
      0.2;
    return total.toFixed(7);
  };

  const updateWhitelistMultiple = async (
    wallets: string[],
    collection: string,
    whitelist = true
  ) => {
    const contract = SimpleNftCollectionV2.fromAddress(
      Address.parse(collection)
    );
    const addressesCell = makeSnakeAddressCellPerOne(
      wallets.map(w => Address.parse(w))
    );
    const actionValue = whitelist
      ? whitelistActionPriceAdd
      : whitelistActionPriceRemove;
    const value = calcMassUpdateValue(wallets.length, whitelist);
    const opened = client?.open(
      contract
    ) as OpenedContract<SimpleNftCollectionV2>;
    if (!opened) {
      return false;
    }
    sendGtag("mass_whitelist", {
      collection,
    });
    try {
      await opened?.send(
        sender,
        {
          value: toNano(value),
        },
        {
          $$type: "MassUpdateWhiteList",
          addresses: addressesCell,
          add: whitelist,
          spendPerAddress: toNano(actionValue),
        }
      );
      return true;
    } catch (e: any) {
      console.log(e);
      // Check for specific error messages that indicate user cancellation
      if (
        e?.message?.includes("cancelled") ||
        e?.message?.includes("rejected") ||
        e?.message?.includes("denied") ||
        e?.name === "UserRejectsError" ||
        e?.code === 4001
      ) {
        // User specifically cancelled the transaction
        throw new Error("Transaction was cancelled by user");
      }
      return false;
    }
  };

  // Restructuring mint to avoid hooks inside async functions
  // ToDo: !!! Call Mint from profile for whitelisted xollections
  const mint = async (address: string, profile?: string) => {
    console.log("Mint item called, collection:", address);
    address = address.split("?")[0];
    if (!wallet) {
      return false;
    }
    sendGtag("mint_nft", {
      collection: address,
      wallet: sender.address,
    });
    try {
      const contract = SimpleNftCollectionV2.fromAddress(
        Address.parse(address)
      );
      const opened = client?.open(
        contract
      ) as OpenedContract<SimpleNftCollectionV2>;
      const masterData = await getCollectionMasterData(address);

      const collectionData = await backend.getCollectionData(address);
      const nextNftAddress = collectionData
        ? collectionData.next_item_address
        : null;

      await sleep(1101);
      // For collection with profiles
      const profileCondition =
        masterData.enable_whitelist ||
        (masterData.user_item_limit && masterData.user_item_limit > 0);

      if (profileCondition) {
        if (!profile) {
          return false;
        }
        const profileContract = BuyerProfile.fromAddress(
          Address.parse(profile)
        );
        const openedProfile = client?.open(
          profileContract
        ) as OpenedContract<BuyerProfile>;
        await openedProfile?.send(
          sender,
          { value: BigInt(masterData.price) },
          "Mint"
        );
      }

      console.log("Nft address info:", collectionData, nextNftAddress);

      if (!profileCondition)
        await opened?.send(sender, { value: BigInt(masterData.price) }, "Mint");
      await sleep(1101);
      addNft({
        collection: address,
        address: nextNftAddress?.toString() || "",
      });

      try {
        console.log("Set timer to reindex");
        if (nextNftAddress)
          setTimeout(() => {
            backend.getItemData(nextNftAddress.toString());
          }, 30000);
      } catch (e) {
        console.log(e);
      }

      return true;
    } catch (e) {
      console.log("Error: ", e);
      return false;
    }
  };

  return {
    balance,
    getMasterData,
    getAddressBalance,
    getNftAddressByIndex,
    getNftContent,
    getCollectionData,
    requestProfile,
    updateWhitelist,
    calcMassUpdateValue,
    updateWhitelistMultiple,
    withdrawEstimate,
    mint,
  };
}
